import { AiChatApiError } from '../../../services/apis/aiChat'

/**
 * Single source of user-facing chat error copy. Server-supplied error text is
 * never rendered: the transport carries a code, this module owns the wording.
 */
export type ChatErrorCode =
    | 'AUTH_EXPIRED'
    | 'RATE_LIMITED'
    | 'TIMEOUT'
    | 'UPSTREAM_UNAVAILABLE'
    | 'CONNECTION_LOST'
    | 'CONVERSATION_GONE'
    | 'CONVERSATION_LOAD_FAILED'
    | 'CHAT_START_FAILED'
    | 'CHART_UPDATE_FAILED'
    | 'FILTER_UNDO_FAILED'
    | 'NOT_UNDERSTOOD'
    | 'SURFACE_UNAVAILABLE'
    | 'ROUTING_FAILED'
    | 'NAVIGATION_CANCELLED'

const COPY: Record<ChatErrorCode, string> = {
    AUTH_EXPIRED: 'Your session expired. Sign in again to continue.',
    RATE_LIMITED: 'Too many requests right now. Try again in a few seconds.',
    TIMEOUT: 'That took too long. Try a simpler request.',
    UPSTREAM_UNAVAILABLE: 'Something went wrong on our side. Try again.',
    CONNECTION_LOST: 'Connection unavailable. Try again in a moment.',
    CONVERSATION_GONE: 'This chat is no longer available.',
    CONVERSATION_LOAD_FAILED: 'This chat could not be opened. Try again.',
    CHAT_START_FAILED: 'A new chat could not be started. Try again.',
    CHART_UPDATE_FAILED: 'Some chart changes could not be applied.',
    FILTER_UNDO_FAILED: 'That filter change could not be undone.',
    NOT_UNDERSTOOD:
        "I couldn't work out what to change. Try naming the indicator or setting directly.",
    SURFACE_UNAVAILABLE: 'That view is not ready yet. Try again in a moment.',
    ROUTING_FAILED: 'Could not route that request. Try again.',
    NAVIGATION_CANCELLED: 'Navigation was cancelled.',
}

/** Codes the worker is allowed to send; anything else is treated as unknown. */
const WIRE_CODES: ChatErrorCode[] = [
    'AUTH_EXPIRED',
    'RATE_LIMITED',
    'TIMEOUT',
    'UPSTREAM_UNAVAILABLE',
]

export function chatErrorText(code: ChatErrorCode): string {
    return COPY[code] ?? COPY.UPSTREAM_UNAVAILABLE
}

/**
 * Classifies a code received from the worker. Unrecognised values fall back to
 * a generic message so a new server-side code can never reach the user as text.
 */
export function chatErrorCodeFromWire(raw: unknown): ChatErrorCode {
    const value = typeof raw === 'string' ? raw.toUpperCase() : ''
    const match = WIRE_CODES.find((code) => code === value)
    return match ?? 'UPSTREAM_UNAVAILABLE'
}

export function chatErrorTextFromWire(raw: unknown): string {
    return chatErrorText(chatErrorCodeFromWire(raw))
}

/**
 * Classifies a thrown request failure. `fallback` covers the operation-specific
 * case (e.g. starting a chat) once status-driven cases are ruled out.
 */
export function chatErrorCodeFromException(
    cause: unknown,
    fallback: ChatErrorCode,
): ChatErrorCode {
    if (cause instanceof AiChatApiError) {
        if (cause.status === 401 || cause.status === 403) return 'AUTH_EXPIRED'
        if (cause.status === 429) return 'RATE_LIMITED'
        if (cause.status === 404) return 'CONVERSATION_GONE'
        if (cause.status === 408 || cause.status === 504) return 'TIMEOUT'
        return fallback
    }
    if (cause instanceof TypeError) return 'CONNECTION_LOST'
    return fallback
}

/**
 * Sends the underlying failure to the console — the only place full detail is
 * allowed — and returns the copy to display.
 */
export function reportChatError(
    scope: string,
    code: ChatErrorCode,
    cause?: unknown,
): string {
    if (cause !== undefined) {
        console.error(`[${scope}] ${code}`, cause)
    } else {
        console.error(`[${scope}] ${code}`)
    }
    return chatErrorText(code)
}
